import React, { useContext } from "react";
import img from "../assets/images/contact-form.webp";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import { SpinnerContext } from "./SpinnerContext";
import { companyDetails } from "../data/constant";
import CountryPhoneInput from "./CountryPhoneInput";
import { Send, User, Mail, Phone, Building, MessageSquare } from "lucide-react";

const ContactForm = ({ headline, id }) => {
  const { setSpinner } = useContext(SpinnerContext);
  const navigate = useNavigate();
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "all",
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      company: "",
      message: "",
      _hp_company_url: "",
    },
  });

  // handle form submit click
  const handleFormSubmit = async (values) => {
    setSpinner(true);

    const payload = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      company: values.company || "N/A",
      message: values.message,
      _hp_company_url: values._hp_company_url || "",
      subject: "Contact Form Submission - PANTHM AI Labs"
    };

    try {
      const response = await fetch("https://panthm.com/api/send-email.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const res = await response.json();
      if (res.error) {
        toast.error(res.error);
      } else {
        toast.success("Email sent successfully!");
        reset();
        navigate("/thank-you");
      }
    } catch (error) {
      toast.error(error.message || "Failed to send email");
    } finally {
      setSpinner(false);
    }
  };
  return (
    <section id={id} className="py-12 md:py-20 bg-slate-50 dark:bg-[#0a0a0a] border-t border-slate-200/60 dark:border-white/5">
      <div className="wrapper">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div data-aos="fade-up" className="space-y-6">
            <div>
              <h2 className="section-heading text-left mb-3">
                {headline ? headline : "Let’s Build Something Great Together"}
              </h2>
              <p className="text-slate-600 dark:text-slate-300 text-base sm:text-lg leading-relaxed">
                Whether you need a cutting-edge web platform, a high-performance mobile app, or autonomous AI systems, our team is ready to deliver.
              </p>
            </div>

            {/* WhatsApp Quick Connect */}
            <a
              href={`https://wa.me/${companyDetails.phone.replace(/[^0-9]/g, "")}?text=Hi%20PANTHM%20AI%20Labs%2C%20I%27d%20like%20to%20discuss%20a%20project.`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-3.5 sm:py-4 rounded-2xl bg-[#25D366] text-white font-semibold hover:bg-[#1ebe5e] transition-all duration-200 shadow-lg shadow-[#25D366]/20 hover:-translate-y-0.5 w-full sm:w-auto justify-center sm:justify-start text-sm sm:text-base"
            >
              <MessageSquare size={20} />
              Chat Instantly on WhatsApp
            </a>
            <p className="text-slate-400 text-xs sm:text-sm -mt-2">or fill the form below ↓</p>

            <div className="bg-white dark:bg-[#07090e] p-6 sm:p-8 rounded-2xl shadow-lg border border-slate-200/80 dark:border-white/10">
              <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
                {/* Invisible honeypot field for bot spam interception */}
                <input
                  type="text"
                  tabIndex="-1"
                  autoComplete="off"
                  style={{ display: "none", opacity: 0, position: "absolute", left: "-9999px" }}
                  {...register("_hp_company_url")}
                />
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <input
                      type="text"
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-[#0c0f17] border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-sm sm:text-base"
                      placeholder="Full Name"
                      {...register("name", {
                        required: "Full name is required",
                        validate: (val) => val.trim() !== "" || "Full name is required",
                      })}
                    />
                    <small className="text-red-500 text-xs">{errors.name?.message}</small>
                  </div>

                  <div className="space-y-1">
                    <input
                      type="text"
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-[#0c0f17] border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-sm sm:text-base"
                      placeholder="Company (Optional)"
                      {...register("company")}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <input
                      type="email"
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-[#0c0f17] border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-sm sm:text-base"
                      placeholder="Email Address"
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                          message: "Entered email is invalid",
                        },
                      })}
                    />
                    <small className="text-red-500 text-xs">{errors.email?.message}</small>
                  </div>

                  <div className="space-y-1">
                    <Controller
                      name="phone"
                      control={control}
                      rules={{
                        required: "Phone number is required",
                        validate: (val) => (val && val.trim().length > 6) || "Phone number is required",
                      }}
                      render={({ field }) => (
                        <CountryPhoneInput
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Phone Number"
                        />
                      )}
                    />
                    <small className="text-red-500 text-xs ml-1">{errors.phone?.message}</small>
                  </div>
                </div>

                <div className="space-y-1">
                  <textarea
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-[#0c0f17] border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all min-h-[120px] text-sm sm:text-base"
                    placeholder="Tell us about your project..."
                    {...register("message", {
                      required: "Message is required",
                      validate: (val) => val.trim() !== "" || "Message is required",
                    })}
                  />
                  <small className="text-red-500 text-xs">{errors.message?.message}</small>
                </div>

                <button
                  disabled={isSubmitting}
                  className="w-full primary-btn py-3.5 sm:py-4 rounded-xl flex items-center justify-center gap-2 group text-sm sm:text-base font-semibold"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                  {!isSubmitting && <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
                </button>

                {/* Enterprise Trust & Mutual NDA Guarantee Badges */}
                <div className="pt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] sm:text-xs text-slate-500 dark:text-slate-400">
                  <span className="flex items-center gap-1">
                    <span className="text-emerald-500">🔒</span> 100% Mutual NDA
                  </span>
                  <span className="hidden sm:inline">•</span>
                  <span className="flex items-center gap-1">
                    <span className="text-blue-500">⚡</span> 24h Response SLA
                  </span>
                  <span className="hidden sm:inline">•</span>
                  <span className="flex items-center gap-1">
                    <span className="text-purple-500">🛡️</span> GDPR & ISO-Ready
                  </span>
                </div>
              </form>
            </div>
          </div>

          {/* Right Hero Visual Card on Desktop */}
          <div data-aos="fade-up" className="relative hidden lg:block h-full min-h-[580px] rounded-2xl overflow-hidden shadow-lg border border-slate-200/80 dark:border-white/10">
            <img 
              src={img} 
              alt="Contact PANTHM AI Labs" 
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
            <div className="absolute bottom-0 left-0 w-full p-8 md:p-10 text-white space-y-3">
              <span className="px-3 py-1 rounded-full bg-primary/20 border border-primary/30 text-primary text-xs font-semibold uppercase tracking-wider">
                Enterprise AI & Engineering
              </span>
              <h4 className="text-2xl font-bold">Ready to Innovate?</h4>
              <p className="text-slate-200 text-sm leading-relaxed">
                Join forward-thinking businesses scaling faster with PANTHM AI Labs’ custom software and AI systems.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
